import {useMemo} from 'react';
import useCustomLocation from './useCustomLocation.hook';
import {useTranslation} from '@sdb/ui/translate/hooks/index';
import {camelToKebab} from '../utils/stringUtils';
import type {NavigationRailMenuDataItem} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';
import type {MenuViewModel} from '../../generated/src/types';
import type {IconType} from '@sdb/ui/icons/icon-types';

export const useNavigation = (menu: MenuViewModel | undefined) => {
    const {t} = useTranslation();
    const location = useCustomLocation();
    const currentPath = location.pathname;

    const navigate = (url: string) => {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new CustomEvent('location-changed'));
    };

    const {navData, selectedId} = useMemo(() => {
        if (!menu?.menuItems) {
            return {navData: [], selectedId: null};
        }

        let currentSelectedId: string | null = null;

        const navItems = menu.menuItems.map((item) => {
            const updatedSubmenu = item.subMenuItems
                ? item.subMenuItems.map((subItem) => {
                      const subItemHref = subItem.href || '';
                      // For root path, only match exact '/'
                      if (subItemHref === '/' && currentPath === '/') {
                          currentSelectedId = subItemHref;
                      } else if (subItemHref !== '/') {
                          // Match if path exactly matches or starts with the href followed by a slash
                          if (
                              currentPath === subItemHref ||
                              currentPath.startsWith(subItemHref + '/')
                          ) {
                              currentSelectedId = subItemHref;
                          }
                      }

                      return {
                          id: subItemHref,
                          href: subItemHref || undefined,
                          label: t(subItem.label) || undefined,
                          onClick: () => {},
                      };
                  })
                : undefined;

            // Only check main menu if no submenu item was selected
            if (!currentSelectedId) {
                const itemHref = item.href || '';
                // For root path, only match exact '/'
                if (itemHref === '/' && currentPath === '/') {
                    currentSelectedId = itemHref;
                } else if (itemHref !== '/') {
                    // Match if path exactly matches or starts with the href followed by a slash
                    if (currentPath === itemHref || currentPath.startsWith(itemHref + '/')) {
                        currentSelectedId = itemHref;
                    }
                }
            }

            return {
                href: item.href || undefined,
                id: item.href || '',
                label: t(item.label),
                iconType: camelToKebab(item.iconType) as IconType,
                submenu: updatedSubmenu,
                onClick: () => {},
            } as NavigationRailMenuDataItem;
        });

        return {navData: navItems, selectedId: currentSelectedId};
    }, [currentPath, menu, t]);

    return {
        navData,
        selectedId,
        navigate,
    };
};
